import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Panel, Section, StateView, useAuth, useToast } from "@ozpanel/ui";
import { useQueryClient } from "@tanstack/react-query";
import { ActionCard, NumberField, SelectField, StatusPill } from "@ozpanel/kit";

/**
 * WHO IS ALIVE, AND WHO SUCCEEDS WHOM — rendered from ONE server-side computation.
 *
 * These two facts are not separate and must never be fetched separately. The order is a standing
 * decision made in advance; liveness decides which ranked node is actually REACHED. A node that is
 * down keeps its rank and is skipped — it is never reordered, because an order that reshuffles as
 * nodes flap is not a pre-authorisation.
 *
 * Computing them apart is what produced four separate outages, each reporting healthy while it was
 * wrong: a node dead for hours left in mail.<domain> so a share of every inbound SMTP connection
 * went nowhere; a Galera arbitrator reconciler that read three registry rows as "odd, no arbitrator
 * needed" and stopped the one two survivors depended on; an arbitrator whose recorded home was a
 * machine powered off for a day, so both survivors deferred to a corpse; and a Redis sentinel
 * quorum that counted three members, removed the extra sentinel, and left Redis unable to fail over
 * at all.
 *
 * Fetching liveness and succession as two queries here would put that same disagreement in the
 * browser. /cluster/state answers both, and the alerts below are derived from the SAME numbers the
 * table renders, so a banner can never contradict the row beneath it.
 */
interface StateNode {
  node: string;
  /** Always one of two values. A node is alive or it is dead; there is no third state. */
  up: boolean;
  judged: boolean;
  down_for_seconds: number;
  mesh_addr: string;
  public_addr: string;
  /** -1 when the node is not in the succession order at all. */
  succession_rank: number;
  pinned: boolean;
  reason: string;
}
interface ClusterStateData {
  self: string;
  primary: string;
  declaration_seq: number;
  nodes: StateNode[];
  alive: number;
  members: number;
  effective_successor: string;
  live_successors: number;
  quorum_majority: number;
  has_quorum: boolean;
  note: string;
}
interface StateAlert {
  key: string;
  priority: string;
  message: string;
}
/**
 * Per-plane role agreement: what each DAEMON says about who is master, judged against the
 * declaration. Measured fresh on every fetch. A plane that DISAGREES is a cluster with two truths,
 * and every handover, failback and deploy refuses until it is one again.
 */
interface PlaneAgreement {
  plane: string;
  reports: string;
  status: "agrees" | "DISAGREES" | "unmeasured" | "no-master-concept";
  detail?: string;
}
/**
 * The standing succession order — identical from every node. The primary is listed first with its
 * OWN rank (where it sits when it is not primary; a pin there is what brings it back after a
 * failback), then the successors in the order a failover reaches them. Liveness is beside the
 * order, not in it: a down node keeps its place and is skipped.
 */
interface SuccessionRow {
  node: string;
  /** The standing rank — the column, what an operator set. Unique. 0 = unranked (join order). */
  rank: number;
  /** Position among successors at failover time. Derived. */
  order: number;
  role: "primary" | "successor";
  pinned: boolean;
  up: boolean;
  reason: string;
  evicted: boolean;
}
interface SuccessionData {
  primary: string;
  effective_successor: string;
  order: SuccessionRow[];
  warning?: string;
}
interface FailbackData {
  primary: string;
  seq: number;
  preset_primary: string;
  preset_primary_note?: string;
  nodes: Record<string, { policy: string; explicit: boolean }>;
}
interface MasterData {
  has_master?: boolean;
  node_uuid?: string;
  node_name: string;
  this_node: string;
  note?: string;
  verified: boolean;
  am_i_leader: boolean;
  /** Where the role belongs (the seat) beside where it is (cluster/master_seat.go). */
  master_seat?: {
    seat: string | null;
    master: string | null;
    on_seat: boolean | null;
    off_seat_since?: string;
    off_seat_seconds?: number;
    why_not_home?: string;
    note?: string;
  };
}
/** A role transition — the replicated row every node and console reads while a move is in progress. */
interface Transition {
  id: number;
  kind: string;
  from: string;
  to: string;
  started_by: string;
  started_at: string;
  expires_at: string;
  step: string;
  done_at?: string;
  outcome?: string;
  detail?: string;
}
interface HandoverStep {
  step: string;
  ok: boolean;
  detail?: unknown;
  error?: string;
}
interface HandoverResult {
  seat_moved?: boolean;
  seat?: string;
  handed_over?: boolean;
  primary?: string;
  ran_on?: string;
  steps?: HandoverStep[];
  planes?: RoleAgreement;
  warning?: string;
  note?: string;
}
/** What this node's role executor is doing: the one place a role is applied to the planes. */
interface RoleExecutor {
  phase: "idle" | "applying";
  seq?: number;
  reason?: string;
  since?: string;
  waiting: number;
  last_seq: number;
  last_reason?: string;
  last_at?: string;
  last_took_ms: number;
  applies: number;
  shared: number;
  dropped: number;
  takeover_of?: string;
  takeover_due?: string;
  takeover_in_seconds?: number;
}

interface RoleAgreement {
  declared_primary: string;
  planes: PlaneAgreement[];
  disagree?: string[];
  unmeasured?: string[];
  agreed: boolean;
}

function since(seconds: number): string {
  if (!seconds || seconds < 60) return `${Math.max(0, Math.round(seconds))}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 360) / 10}h`;
}

export function ClusterStatePanel() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["cluster-state"],
    queryFn: () =>
      client.apiFetch<{
        state: ClusterStateData;
        alerts: StateAlert[] | null;
        planes: RoleAgreement;
        authority: Record<string, string>;
        transition: Transition | null;
        recent_transitions: Transition[] | null;
        role_executor?: RoleExecutor;
        note: string;
      }>(
        "/cluster/state",
      ),
    refetchInterval: 15000,
  });

  if (q.isLoading || q.isError || !q.data?.state) {
    return (
      <StateView
        loading={q.isLoading}
        error={q.isError ? "Cluster state could not be read" : undefined}
        empty={!q.isLoading && !q.isError && !q.data?.state}
        emptyMessage="This node cannot compute the cluster state."
      />
    );
  }
  const s = q.data.state;
  const alerts = q.data.alerts ?? [];
  const planes = q.data.planes;
  const transition = q.data.transition;
  const recent = q.data.recent_transitions ?? [];
  const authority = q.data.authority ?? {};
  const exec = q.data.role_executor;

  return (
    <Panel>
      {transition ? (
        <Section
          title={`${transition.kind.toUpperCase()} IN PROGRESS: ${transition.from || "—"} → ${transition.to}`}
          hint={`step: ${transition.step} · started by ${transition.started_by} · expires ${new Date(transition.expires_at).toLocaleTimeString()} if it does not finish. Handover, publish and automatic failback all refuse until this closes.`}
          actions={<StatusPill tone="warn">moving</StatusPill>}
        >
          <span style={{ opacity: 0.7, fontSize: 13 }}>id {transition.id}</span>
        </Section>
      ) : null}
      {exec?.takeover_of ? (
        <Section
          title={`Waiting to take over from ${exec.takeover_of}`}
          hint={`This node judged ${exec.takeover_of} gone and is waiting out the confirmation pause before it promotes. Planes still pointing at ${exec.takeover_of} until then are expected, not failed.`}
          actions={
            <StatusPill tone="warn">
              {exec.takeover_in_seconds ? `promotes in ${exec.takeover_in_seconds} s` : "promoting now"}
            </StatusPill>
          }
        >
          <span style={{ opacity: 0.7, fontSize: 13 }}>last applied declaration {exec.last_seq}</span>
        </Section>
      ) : null}
      {exec && exec.phase === "applying" ? (
        <Section
          title={`Applying declaration ${exec.seq} to this node's services`}
          hint={`${exec.reason ?? ""}${exec.waiting ? ` · ${exec.waiting} more request(s) waiting` : ""}. One apply runs at a time.`}
          actions={<StatusPill tone="warn">applying</StatusPill>}
        >
          <span style={{ opacity: 0.7, fontSize: 13 }}>last applied declaration {exec.last_seq}</span>
        </Section>
      ) : null}
      <Section
        title={`${s.alive} of ${s.members} answering`}
        hint={s.note}
        actions={
          <StatusPill
            tone={s.has_quorum ? "ok" : "bad"}
          >
            {s.has_quorum ? `quorum ${s.alive}/${s.members}` : `below quorum (need ${s.quorum_majority})`}
          </StatusPill>
        }
      >
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 8 }}>
          <span>
            primary <b>{s.primary || "none"}</b>
          </span>
          <span>
            next <b>{s.effective_successor || "NOBODY"}</b>
          </span>
          <span style={{ opacity: 0.7 }}>declaration seq {s.declaration_seq}</span>
        </div>

        {s.nodes.map((n) => (
          <div
            key={n.node}
            style={{
              display: "flex",
              gap: 10,
              alignItems: "baseline",
              padding: "6px 0",
              borderTop: "1px solid var(--bd,#222)",
            }}
          >
            <span style={{ minWidth: 190, fontFamily: "monospace" }}>{n.node}</span>
            <StatusPill tone={n.up ? "ok" : "bad"}>
              {n.up ? "up" : `down ${since(n.down_for_seconds)}`}
            </StatusPill>
            <span>
              {n.succession_rank > 0 ? (
                <>
                  <b>#{n.succession_rank}</b>
                  {n.pinned ? <span style={{ opacity: 0.6 }}> (pinned)</span> : null}
                  {/* A dead node KEEPS its rank and is skipped — never reordered. */}
                  {!n.up ? <span style={{ opacity: 0.6 }}> — skipped</span> : null}
                </>
              ) : n.node === s.primary ? (
                <b>PRIMARY</b>
              ) : (
                <span style={{ opacity: 0.6 }}>not in the order</span>
              )}
            </span>
            {authority[n.node] ? (
              <StatusPill
                tone={
                  authority[n.node] === "master_holder" ? "accent"
                  : authority[n.node] === "master_candidate" ? "ok"
                  : authority[n.node] === "none" ? "neutral"
                  : "bad"
                }
              >
                {authority[n.node] === "master_holder" ? "MASTER · signs"
                  : authority[n.node] === "master_candidate" ? "armed successor"
                  : authority[n.node] === "master_candidate_UNARMED" ? "UNARMED successor — cannot sign"
                  : authority[n.node] === "key_without_rank" ? "holds key, not ranked"
                  : authority[n.node] === "master_holder_NO_KEY" ? "MASTER WITHOUT KEY"
                  : "no authority"}
              </StatusPill>
            ) : null}
            <span style={{ opacity: 0.7 }}>{n.reason}</span>
          </div>
        ))}
      </Section>

      <SuccessionSection transition={transition} planes={planes} />

      <Section
        title="Planes"
        hint={
          planes?.agreed
            ? `every plane with a master concept agrees (${planes.planes.filter((p) => p.status === "agrees").length} checked, ${planes.planes.filter((p) => p.status === "no-master-concept").length} have none); primary ${planes.declared_primary}`
            : "the daemons do not all agree with the declaration — handover, failback and deploy refuse until they do"
        }
        actions={
          planes ? (
            <StatusPill tone={planes.agreed ? "ok" : planes.disagree?.length ? "bad" : "warn"}>
              {planes.agreed ? "one state" : planes.disagree?.length ? "SPLIT" : "incomplete"}
            </StatusPill>
          ) : null
        }
      >
        {(planes?.planes ?? []).map((p) => (
          <div key={p.plane} style={{ display: "flex", gap: 10, alignItems: "baseline", padding: "4px 0" }}>
            <span style={{ minWidth: 110, fontFamily: "monospace" }}>{p.plane}</span>
            <StatusPill
              tone={p.status === "agrees" ? "ok" : p.status === "DISAGREES" ? "bad" : p.status === "unmeasured" ? "warn" : "neutral"}
            >
              {p.status === "no-master-concept" ? "no master concept" : p.status}
            </StatusPill>
            <span style={{ opacity: 0.7, fontFamily: "monospace" }}>
              {p.reports?.startsWith("master:") ? `follows master: ${p.reports.slice(7)}` : p.reports?.startsWith("node:") ? p.reports.slice(5) : p.reports || "—"}
            </span>
            {p.detail ? <span style={{ opacity: 0.7 }}>{p.detail}</span> : null}
          </div>
        ))}
      </Section>

      {recent.length > 0 ? (
        <Section title="Transitions" hint="every role move, with its outcome — the same record from every node">
          {recent.map((t) => (
            <div key={t.id} style={{ display: "flex", gap: 10, alignItems: "baseline", padding: "3px 0", fontSize: 13 }}>
              <span style={{ opacity: 0.6, minWidth: 70 }}>{new Date(t.started_at).toLocaleTimeString()}</span>
              <span style={{ fontFamily: "monospace", minWidth: 70 }}>{t.kind}</span>
              <span style={{ fontFamily: "monospace" }}>{t.from || "—"} → {t.to}</span>
              <StatusPill tone={t.outcome === "complete" ? "ok" : t.outcome === "partial" ? "warn" : !t.outcome ? "warn" : "bad"}>
                {t.outcome ?? `running: ${t.step}`}
              </StatusPill>
              <span style={{ opacity: 0.6 }}>by {t.started_by}</span>
              {t.detail ? <span style={{ opacity: 0.6 }}>{t.detail.slice(0, 120)}</span> : null}
            </div>
          ))}
        </Section>
      ) : null}

      <Section
        title="Needs attention"
        hint={
          alerts.length === 0
            ? "Every ranked node that could take over is answering."
            : "These are states where the cluster has silently lost the ability to recover — each one looks healthy on every other indicator for as long as it is true."
        }
      >
        {alerts.length === 0 ? (
          <StatusPill tone="ok">nothing to report</StatusPill>
        ) : (
          alerts.map((a) => (
            <div key={a.key} style={{ display: "flex", gap: 10, padding: "6px 0" }}>
              <StatusPill tone={a.priority === "urgent" ? "bad" : a.priority === "info" ? "ok" : "warn"}>
                {a.priority}
              </StatusPill>
              <span>{a.message}</span>
            </div>
          ))
        )}
      </Section>
    </Panel>
  );
}


/**
 * SUCCESSION, SEAT AND SIGNING AUTHORITY — three different facts that were all called "primary".
 *
 * - The WORKLOAD PRIMARY runs PostgreSQL, Redis, the arbitrator: node100 today.
 * - The SIGNING AUTHORITY ("master") holds the cluster CA key and signs declarations, certificates
 *   and releases: node11 today. Deliberately decoupled (CLAUDE.md rule 15): a wrongly moved master
 *   is corrected by claiming it back; two nodes accepting writes is not corrected at all.
 * - The SEAT (preset primary) is whose the workload is by default — the node that takes it back
 *   automatically after an outage, if its failback policy says so.
 *
 * Every control here calls a cluster-wide API: a rank, seat or policy set on any node's console is
 * the same signed fact on every node. These APIs existed with no UI — rule 8.
 */
function SuccessionSection({ transition, planes }: { transition: Transition | null; planes?: RoleAgreement }) {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const succ = useQuery({
    queryKey: ["cluster-succession"],
    queryFn: () => client.apiFetch<SuccessionData>("/cluster/succession"),
    refetchInterval: 15000,
  });
  const fb = useQuery({
    queryKey: ["cluster-failback"],
    queryFn: () => client.apiFetch<FailbackData>("/cluster/failback"),
    refetchInterval: 15000,
  });
  const master = useQuery({
    queryKey: ["cluster-master"],
    queryFn: () => client.apiFetch<MasterData>("/cluster/master"),
    refetchInterval: 30000,
  });
  const refresh = () => {
    for (const k of ["cluster-succession", "cluster-failback", "cluster-state", "cluster-nodes"]) {
      void qc.invalidateQueries({ queryKey: [k] });
    }
  };
  // The call is written at each button, so the method, path and body are read where they are sent
  // (internal/console: a path handed to a wrapper is invisible to the reach and body checks).
  const act = (label: string, call: Promise<unknown>) =>
    call
      .then(() => {
        notify({ kind: "success", title: label });
        refresh();
      })
      .catch((e: unknown) => notify({ kind: "error", title: `${label} refused`, body: e instanceof Error ? e.message : String(e) }));

  const preset = fb.data?.preset_primary ?? "";
  const rows = succ.data?.order ?? [];
  // Whether THIS console can move the seat: only the master (signing authority) may. Elsewhere a
  // handover leaves the seat where it is — and an automatic seat reclaims within a minute. The
  // button says which it will be, before the click.
  const hereIsMaster = !!master.data?.am_i_leader;
  const seatPolicy = preset ? fb.data?.nodes?.[preset]?.policy ?? "manual" : "manual";
  const handoverLabel = (node: string) => {
    if (hereIsMaster) return "make primary (moves seat + rank #1)";
    if (!preset || preset === node) return "hand over (rank #1)";
    if (seatPolicy === "automatic") return `hand over — seat stays ${preset}, will reclaim in ~1m`;
    return `hand over — seat stays ${preset}`;
  };
  const [handover, setHandover] = React.useState<{ node: string; result?: HandoverResult; error?: string; running?: boolean } | null>(null);
  const handTo = (node: string) => {
    setHandover({ node, running: true });
    client
      .apiFetch<HandoverResult>("/cluster/handover-to", { method: "POST", body: JSON.stringify({ node }) })
      .then((res) => {
        setHandover({ node, result: res });
        notify({ kind: res.planes?.agreed ? "success" : "error", title: res.planes?.agreed ? `${node} is primary — every plane followed` : `${node} took the seat but not every plane followed` });
        refresh();
      })
      .catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : String(e);
        setHandover({ node, error: msg });
        notify({ kind: "error", title: `handover to ${node} refused`, body: msg });
      });
  };

  return (
    <Section
      title="Succession"
      hint={
        succ.data?.warning ??
        fb.data?.preset_primary_note ??
        (succ.data?.effective_successor
          ? `if ${succ.data.primary} fails, ${succ.data.effective_successor} takes over${preset ? `; the seat is ${preset}` : ""}`
          : undefined)
      }
      actions={
        master.data ? (
          <span style={{ fontSize: 12, opacity: 0.8 }}>
            signing authority (master): <b>{master.data.node_name}</b>
            {master.data.master_seat?.on_seat === false ? (
              <span style={{ color: "var(--warn,#d97706)" }}>
                {" "}— away from its seat <b>{master.data.master_seat.seat}</b>
                {master.data.master_seat.off_seat_since ? ` since ${master.data.master_seat.off_seat_since}` : ""}
                {master.data.master_seat.why_not_home ? ` (${master.data.master_seat.why_not_home})` : " (the master's console says why)"}
              </span>
            ) : master.data.master_seat?.on_seat === true ? (
              <span style={{ opacity: 0.7 }}> — on its seat</span>
            ) : null}
            {!hereIsMaster ? (
              <span style={{ opacity: 0.7 }}> · rank, seat and policy are changed on its console, not here</span>
            ) : null}
          </span>
        ) : null
      }
    >
      <StateView loading={succ.isLoading} error={succ.isError ? "succession could not be read" : undefined}>
        {rows.map((r) => {
          const policy = fb.data?.nodes?.[r.node]?.policy ?? "manual";
          const isSeat = preset === r.node;
          return (
            <div
              key={r.node}
              style={{ display: "flex", gap: 10, alignItems: "center", padding: "6px 0", borderTop: "1px solid var(--bd,#222)", flexWrap: "wrap" }}
            >
              <span style={{ minWidth: 90, fontFamily: "monospace" }}>{r.node}</span>
              <StatusPill tone={r.role === "primary" ? "accent" : "neutral"}>
                {r.role === "primary" ? "PRIMARY" : `takes over ${r.order === 1 ? "first" : r.order === 2 ? "second" : `${r.order}th`}`}
              </StatusPill>
              <span style={{ opacity: 0.7, fontSize: 12, fontFamily: "monospace" }}>
                {r.pinned ? `rank ${r.rank}` : "unranked"}
              </span>
              <StatusPill tone={r.up ? "ok" : "bad"}>{r.up ? "up" : "down"}</StatusPill>
              {!r.pinned ? <span style={{ opacity: 0.5, fontSize: 12 }}>(join order)</span> : null}
              {isSeat ? <StatusPill tone="accent">seat</StatusPill> : null}
              <span style={{ opacity: 0.6, fontSize: 12 }}>failback: {policy}</span>
              <span style={{ flex: 1 }} />
              {r.role === "successor" && r.up ? (
                <Button size="sm" variant="accent" disabled={!!handover?.running} onClick={() => handTo(r.node)}>
                  {handover?.running && handover.node === r.node ? "handing over…" : handoverLabel(r.node)}
                </Button>
              ) : null}
              {/* MASTER-ONLY: rank, seat and failback policy decide who may claim the master role and
                  who takes the workload on a failure. They are changed where the signing key is. On any
                  other console they are shown disabled with the reason, not hidden — an operator must be
                  able to see what they cannot do here and where they can. */}
              {/* ANY POSITION, not only #1. The server inserts at the chosen rank and shifts the others
                  (SetSuccessionRank), then re-signs the declaration on the same click
                  (KeepSuccessionInStep) — the row replicates, and every node verifies the new signed
                  order itself, so there is nothing to sync by hand. "join order" clears the pin. */}
              <span
                style={{ minWidth: 150 }}
                title={hereIsMaster ? undefined : `only the master (${master.data?.node_name ?? "?"}) may reorder the succession — open its console`}
              >
                <SelectField
                  label="Rank"
                  value={r.pinned ? String(r.rank) : ""}
                  disabled={!hereIsMaster}
                  options={[
                    ...rows.map((_, i) => ({ value: String(i + 1), label: `#${i + 1}` })),
                    { value: "", label: "join order (unpinned)" },
                  ]}
                  onChange={(v) => {
                    if (v === (r.pinned ? String(r.rank) : "")) return;
                    void act(
                      v === "" ? `${r.node} unpinned (join order)` : `${r.node} moved to rank ${v}`,
                      client.apiFetch(`/cluster/nodes/${encodeURIComponent(r.node)}/succession-rank`, {
                        method: "POST",
                        body: JSON.stringify({ rank: v === "" ? null : Number(v) }),
                      }),
                    );
                  }}
                />
              </span>
              {!isSeat ? (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={!hereIsMaster}
                  title={hereIsMaster ? undefined : `only the master (${master.data?.node_name ?? "?"}) may move the seat`}
                  onClick={() =>
                    void act(
                      `${r.node} is the seat`,
                      client.apiFetch("/cluster/preset-primary", { method: "POST", body: JSON.stringify({ node: r.node }) }),
                    )
                  }
                >
                  make seat
                </Button>
              ) : null}
              <Button
                size="sm"
                variant="ghost"
                disabled={!hereIsMaster}
                title={hereIsMaster ? undefined : `only the master (${master.data?.node_name ?? "?"}) may change failback policy`}
                onClick={() =>
                  void act(
                    `${r.node} failback ${policy === "automatic" ? "manual" : "automatic"}`,
                    client.apiFetch(`/cluster/nodes/${encodeURIComponent(r.node)}/failback`, {
                      method: "POST",
                      body: JSON.stringify({ policy: policy === "automatic" ? "manual" : "automatic" }),
                    }),
                  )
                }
              >
                failback → {policy === "automatic" ? "manual" : "automatic"}
              </Button>
            </div>
          );
        })}
      </StateView>
      {handover && !handover.running ? (
        <div style={{ marginTop: 10, padding: 10, border: "1px solid var(--bd,#222)", borderRadius: 6 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
            <b>handover → {handover.node}</b>
            {handover.error ? (
              <StatusPill tone="bad">refused</StatusPill>
            ) : handover.result?.planes?.agreed ? (
              <StatusPill tone="ok">complete — every plane agrees</StatusPill>
            ) : (
              <StatusPill tone="warn">seat moved, planes still following</StatusPill>
            )}
            <span style={{ flex: 1 }} />
            <Button size="sm" variant="ghost" onClick={() => setHandover(null)}>dismiss</Button>
          </div>
          {handover.error ? <div style={{ marginTop: 6, opacity: 0.85 }}>{handover.error}</div> : null}
          {(handover.result?.steps ?? []).map((st) => (
            <div key={st.step} style={{ display: "flex", gap: 8, padding: "3px 0", fontSize: 13 }}>
              <StatusPill tone={st.ok ? "ok" : "bad"}>{st.ok ? "ok" : "failed"}</StatusPill>
              <span style={{ fontFamily: "monospace", minWidth: 120 }}>{st.step}</span>
              <span style={{ opacity: 0.7 }}>{st.error ?? (typeof st.detail === "string" ? st.detail : st.detail ? JSON.stringify(st.detail).slice(0, 160) : "")}</span>
            </div>
          ))}
          {(handover.result?.planes?.planes ?? []).map((p) => (
            <div key={p.plane} style={{ display: "flex", gap: 8, padding: "2px 0", fontSize: 13 }}>
              <StatusPill tone={p.status === "agrees" ? "ok" : p.status === "DISAGREES" ? "bad" : "warn"}>{p.status}</StatusPill>
              <span style={{ fontFamily: "monospace", minWidth: 120 }}>{p.plane}</span>
              <span style={{ opacity: 0.7 }}>{p.reports}{p.detail ? ` — ${p.detail}` : ""}</span>
            </div>
          ))}
          {handover.result?.seat ? <div style={{ marginTop: 6, opacity: 0.85 }}>{handover.result.seat}</div> : null}
          {handover.result?.warning ? <div style={{ marginTop: 6, opacity: 0.85 }}>{handover.result.warning}</div> : null}
        </div>
      ) : null}
      <DeclareWorkload rows={rows} master={master.data} masterError={master.error} transition={transition} planes={planes} onDeclared={refresh} />
    </Section>
  );
}

/** cluster/workload.go WorkloadClaim — its json tags, exactly. */
interface WorkloadClaim {
  seq: number;
  master_uuid: string;
  primary_node: string;
  succession: string[] | null;
  max_lag_bytes: number;
  declared_at: number;
  kind?: string;
  signer_uuid?: string;
  /** Per-node failback policy; the key "_preset_primary" is the SEAT (failback_request.go). */
  failback?: Record<string, string> | null;
}
/** GET /cluster/workload (handleWorkload): the declaration and what THIS node concludes from it. */
interface WorkloadView {
  node: string;
  declaration: WorkloadClaim | null;
  note?: string;
  /** The declaration could not be read or did not verify — then `declaration` is null. */
  error?: string;
  decision?: {
    am_primary: boolean;
    am_successor: boolean;
    succession_rank: number;
    primary: string;
    may_activate: boolean;
    reason: string;
  };
  measured_at?: string;
}
const seatKey = "_preset_primary"; // failback_request.go presetPrimaryKey

/**
 * DECLARE THE WORKLOAD — POST /cluster/workload/declare, the deliberate act of naming the primary by
 * signature. Normally the declaration moves by handover (POST /cluster/handover-to, the buttons above),
 * by automatic succession or by failback; this is the root declaration underneath all of them, and
 * until now only the generic operations form could reach it.
 *
 * What the server does, read from workload.go declareWithFailback (and said in the confirmation):
 *   - refuses unless THIS node is the verified master, and unless a primary is named;
 *   - signs seq = current + 1 with the cluster CA key; the succession is REBUILT from the registry's
 *     ranks (StandingOrder) — the body's list is only the fallback when the ranks cannot be read;
 *   - carries the failback policy (and with it the seat) forward; max lag defaults to 1 MiB when not
 *     positive — NOT to the current value, so the current value is sent explicitly;
 *   - writes the row and nudges every peer to apply it now.
 * What it does NOT do: check that the named node is registered (the select below is the only guard),
 * wait for a transition or for the planes to agree, or demand the incumbent's acknowledgement.
 * AcceptDeclaration (lock 7: a LIVE primary is relieved only by handover) has no production caller, so
 * every node accepts a root declaration that moves a live primary. PostgreSQL is not moved by the
 * declaration either: the PostgreSQL chain (pgchain.go decidePG) promotes the named node only when it is
 * a standby and NO other node answers as primary; if another node still serves, the named node hands
 * the declaration straight back to it (declreconcile.go) — a bounce during which the other planes follow
 * the named node and then return.
 */
function DeclareWorkload({
  rows,
  master,
  masterError,
  transition,
  planes,
  onDeclared,
}: {
  rows: SuccessionRow[];
  master?: MasterData;
  masterError: unknown;
  transition: Transition | null;
  planes?: RoleAgreement;
  onDeclared: () => void;
}) {
  const { client } = useAuth();
  const wq = useQuery({
    queryKey: ["cluster-workload"],
    queryFn: () => client.apiFetch<WorkloadView>("/cluster/workload"),
    refetchInterval: 15000,
  });
  const cur = wq.data?.declaration ?? null;
  const [primary, setPrimary] = React.useState("");
  const [lag, setLag] = React.useState("");

  const nodes = rows.filter((r) => !r.evicted);
  const up = (n: string) => nodes.find((r) => r.node === n)?.up;
  const lagText = lag === "" ? (cur ? String(cur.max_lag_bytes) : "") : lag;
  const lagOK = /^\d+$/.test(lagText.trim());
  const masterName = master?.node_name || "unknown";
  const masterLabel = (uuid: string) =>
    master?.node_uuid && uuid === master.node_uuid ? `${master.node_name} (${uuid.slice(0, 8)})` : uuid || "unknown";
  const seat = cur?.failback?.[seatKey];
  const policies = Object.entries(cur?.failback ?? {}).filter(([k]) => k !== seatKey);

  // declareWithFailback's refusals, in its order, that this console can know beforehand.
  const unavailable = masterError
    ? "Whether this node is the master could not be read (GET /cluster/master), so whether the server would accept a declaration from here is unknown."
    : !master
      ? "Reading who the master is…"
      : !master.am_i_leader
        ? !master.verified && master.has_master
          ? `Only the verified master may declare the workload, and the recorded master (${masterName}) does not verify against this node's cluster CA. ${master.note ?? ""}`
          : `Only the verified master may declare the workload — the master is ${masterName}, this console is on ${master.this_node}. Nothing relays the request: open ${masterName}'s console.`
        : !primary
          ? "Choose the node to name as primary."
          : !lagOK
            ? "Max lag must be a whole number of bytes."
            : undefined;

  const curPrimary = cur?.primary_node ?? "";
  const same = !!primary && primary === curPrimary;
  const preflight: { ok: boolean; label: string; detail?: string }[] = [];
  if (primary) {
    if (!cur) {
      preflight.push({
        ok: !wq.data?.error,
        label: wq.data?.error ? "The current declaration could not be read or verified" : "No declaration exists yet",
        detail: wq.data?.error ?? "this becomes the first one (seq 1); the server refuses if it cannot read the current sequence",
      });
    } else if (same) {
      preflight.push({
        ok: true,
        label: `${primary} is already the declared primary`,
        detail: "a re-declaration: a new seq and the succession rebuilt from the ranks — no role moves",
      });
    } else if (up(curPrimary) === true) {
      preflight.push({
        ok: false,
        label: `${curPrimary}, the current primary, is up`,
        detail: `moving a LIVE primary is a handover (the "hand over" button on ${primary}'s row): it quiesces ${curPrimary} first and carries its signed acknowledgement. The server REFUSES this declaration while ${curPrimary} answers on any of its addresses (lock 7) — use hand over.`,
      });
    } else {
      preflight.push({
        ok: up(curPrimary) === false,
        label: up(curPrimary) === false ? `${curPrimary}, the current primary, is down` : `Whether ${curPrimary} is up is unknown`,
        detail: `the server asks ${curPrimary} on every address and refuses if it answers; if it is gone, ${primary}'s PostgreSQL chain promotes it when it is a standby and no other node answers as primary`,
      });
    }
    if (up(primary) === false) {
      preflight.push({
        ok: false,
        label: `${primary} is down`,
        detail:
          "nothing can be promoted there until it returns; meanwhile every other node's planes follow a node that is not answering, and a PostgreSQL primary still serving elsewhere keeps serving (stopping it would leave no writer)",
      });
    }
    if (transition) {
      preflight.push({
        ok: false,
        label: `A ${transition.kind} is in progress (${transition.from || "—"} → ${transition.to}, step ${transition.step})`,
        detail: "the server refuses a declaration while a transition is open — wait for it to finish or expire",
      });
    }
    if (planes && !planes.agreed) {
      preflight.push({
        ok: false,
        label: "The planes do not all agree with the current declaration",
        detail: `${planes.disagree?.length ? "disagree: " + planes.disagree.join(", ") : "unmeasured: " + (planes.unmeasured ?? []).join(", ")} — the declare path does not check this`,
      });
    }
  }

  return (
    <div style={{ marginTop: 14, paddingTop: 10, borderTop: "1px solid var(--bd,#222)" }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>Declare the workload</div>
      <StateView loading={wq.isLoading} error={wq.isError ? "the current declaration could not be read (GET /cluster/workload)" : undefined}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14 }}>
          <div style={{ fontSize: 13, lineHeight: 1.7 }}>
            <div style={{ opacity: 0.7, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>current declaration (signed)</div>
            {cur ? (
              <>
                <div>
                  seq <b>{cur.seq}</b> · {cur.kind === "succession" ? "succession (node-signed)" : "root (CA-signed)"} · declared{" "}
                  {cur.declared_at ? new Date(cur.declared_at * 1000).toLocaleString() : "unknown"}
                </div>
                <div>
                  primary <b>{cur.primary_node || "none"}</b>
                </div>
                <div>
                  succession <span style={{ fontFamily: "monospace" }}>{(cur.succession ?? []).join(" → ") || "empty"}</span>
                </div>
                <div>
                  master that signed it <span style={{ fontFamily: "monospace" }}>{masterLabel(cur.master_uuid)}</span>
                </div>
                <div>
                  seat <b>{seat || "none"}</b> · failback{" "}
                  {policies.length ? policies.map(([n, p]) => `${n}: ${p}`).join(", ") : "manual everywhere (unset)"}
                </div>
                <div>max lag {cur.max_lag_bytes} bytes</div>
              </>
            ) : (
              <div style={{ opacity: 0.8 }}>{wq.data?.error ?? wq.data?.note ?? "no declaration"}</div>
            )}
            {wq.data?.decision ? (
              <div style={{ opacity: 0.75 }}>
                {wq.data.node} concludes: {wq.data.decision.reason}
              </div>
            ) : null}
          </div>
          <div>
            <SelectField
              label="Primary"
              value={primary}
              onChange={setPrimary}
              disabled={!master?.am_i_leader}
              help="Registered nodes only (evicted ones are left out) — the server refuses an unregistered or evicted name too."
              options={[
                { value: "", label: "choose a node" },
                ...nodes.map((r) => ({
                  value: r.node,
                  label: `${r.node}${r.node === curPrimary ? " (declared primary)" : ""}${r.up ? "" : " — DOWN"}`,
                })),
              ]}
            />
            <NumberField
              label="Max lag"
              unit="bytes"
              value={lagText}
              onChange={setLag}
              disabled={!master?.am_i_leader}
              help="How far behind a successor may be and still take over. Prefilled with the current value; sent as 0 the server would reset it to 1 MiB."
            />
          </div>
        </div>
        <div style={{ marginTop: 10 }}>
          <ActionCard
            title={primary ? `Declare ${primary} primary` : "Declare the workload"}
            description="The root declaration, signed with the cluster CA key on the master. To move a primary that is alive, use hand over above — this is for naming a primary when there is no live one to hand over from, or re-signing the standing order."
            actionLabel="Declare"
            danger="high"
            unavailable={unavailable}
            preflight={preflight}
            confirm={{
              title: `Sign a declaration naming ${primary} primary?`,
              confirmLabel: "Sign and declare",
              body: (
                <div style={{ fontSize: 12.5, lineHeight: 1.6 }}>
                  <p>
                    <b>On {masterName} (this node):</b> signs declaration seq {cur ? cur.seq + 1 : "current + 1"} with the cluster CA key —
                    primary {primary}, max lag {lagText} bytes. The succession is rebuilt from the registry's ranks (the Rank column above),
                    not taken from what is shown here; the failback policy and the seat ({seat || "none"}) are carried forward unchanged. The
                    row replicates, and every peer is nudged to apply it now rather than at its next pass.
                  </p>
                  <p>
                    <b>On every node:</b> the signature is verified and each role plane — Redis, the Galera arbitrator, PgBouncer, Caddy,
                    DNS, cron, JuiceFS, apps — turns to follow {primary}.
                  </p>
                  <p>
                    <b>PostgreSQL is not moved by this.</b> {primary}'s PostgreSQL chain promotes it only if it is a standby and no other
                    node answers as primary. If {curPrimary && curPrimary !== primary ? curPrimary : "another node"} is still serving,{" "}
                    {primary} hands the declaration straight back to it, and the planes return.
                  </p>
                  <p>
                    <b>It refuses</b> an unregistered or evicted node, a declaration while a role transition is open, and moving the primary off a node that still answers (use hand over). <b>It does not</b> quiesce the current primary or check that the planes agree.
                    Moving a live primary is <i>hand over</i>, not this.
                  </p>
                </div>
              ),
            }}
            run={() =>
              client
                .apiFetch<{ declared: WorkloadClaim; note?: string }>("/cluster/workload/declare", {
                  method: "POST",
                  body: JSON.stringify({ primary, succession: cur?.succession ?? [], max_lag_bytes: Number(lagText.trim()) }),
                })
                .then((r) => {
                  onDeclared();
                  return r;
                })
            }
            invalidates={[["cluster-workload"], ["cluster-state"], ["cluster-succession"], ["cluster-failback"]]}
            renderResult={(r) => {
              const res = r as { declared?: WorkloadClaim; note?: string };
              const d = res.declared;
              return (
                <div style={{ lineHeight: 1.7 }}>
                  {d ? (
                    <>
                      <div>
                        seq <b>{d.seq}</b> · primary <b>{d.primary_node}</b> · {d.kind || "root"}
                      </div>
                      <div>
                        succession <span style={{ fontFamily: "monospace" }}>{(d.succession ?? []).join(" → ") || "empty"}</span>
                      </div>
                      <div>
                        max lag {d.max_lag_bytes} bytes · seat {d.failback?.[seatKey] || "none"} · signed by{" "}
                        <span style={{ fontFamily: "monospace" }}>{masterLabel(d.master_uuid)}</span>
                      </div>
                    </>
                  ) : (
                    <pre style={{ whiteSpace: "pre-wrap" }}>{JSON.stringify(r, null, 2)}</pre>
                  )}
                  {res.note ? <div style={{ opacity: 0.8 }}>{res.note}</div> : null}
                  <div style={{ opacity: 0.8 }}>Whether each daemon followed is measured in the Planes section of this page.</div>
                </div>
              );
            }}
          />
        </div>
      </StateView>
    </div>
  );
}
